import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { MeResponse } from '../../types';
import Navbar from '../shared/Navbar';

type Audience = 'all' | 'verified' | 'specific';

interface SendResult {
  success: boolean;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  failed_recipients: string[];
}

const SendEmailPage: React.FC = () => {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [audienceCounts, setAudienceCounts] = useState({
    all_users: 0,
    verified_users: 0,
  });
  const [audience, setAudience] = useState<Audience>('verified');
  const [specificEmail, setSpecificEmail] = useState('');
  const [fromEmail, setFromEmail] = useState('info@teachmenow.ai');
  const [subject, setSubject] = useState('');
  const [textBody, setTextBody] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const current = await api.me();
        setMe(current);
        if (current.authenticated && current.user.is_admin) {
          setAudienceCounts(await api.getAdminEmailAudiences());
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load the email page');
      } finally {
        setLoading(false);
      }
    };
    void bootstrap();
  }, []);

  const recipientCount = useMemo(() => {
    if (audience === 'all') return audienceCounts.all_users;
    if (audience === 'verified') return audienceCounts.verified_users;
    return specificEmail.trim() ? 1 : 0;
  }, [audience, audienceCounts, specificEmail]);

  const audienceDescription = useMemo(() => {
    if (audience === 'all') return `all ${audienceCounts.all_users} registered users`;
    if (audience === 'verified') {
      return `all ${audienceCounts.verified_users} users with verified email addresses`;
    }
    return specificEmail.trim() || 'the specified address';
  }, [audience, audienceCounts, specificEmail]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    if (!textBody.trim() && !htmlBody.trim()) {
      setError('Enter a text body, an HTML body, or both.');
      return;
    }

    const confirmed = window.confirm(
      `Send “${subject.trim()}” to ${audienceDescription}?\n\nThis action cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      setSending(true);
      const response = await api.sendAdminEmail({
        audience,
        specific_email: audience === 'specific' ? specificEmail.trim() : undefined,
        from_email: fromEmail.trim(),
        subject: subject.trim(),
        text_body: textBody,
        html_body: htmlBody,
      });
      setResult(response);
      if (response.success) {
        setSubject('');
        setTextBody('');
        setHtmlBody('');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const isAdmin = me?.authenticated && me.user.is_admin;

  return (
    <div className="app send-email-page">
      <Navbar />
      <main className="app-main">
        <div className="content-container">
          <div className="page-intro">
            <h2>Send Email</h2>
            <p>Send a text and/or HTML email through Amazon SES.</p>
          </div>

          {loading && <div className="ml-subtle">Loading…</div>}

          {!loading && !isAdmin && (
            <div className="admin-guard">
              <div className="admin-guard-title">Admin access required</div>
              <div className="admin-guard-body">You must be signed in as an admin to send email.</div>
              <div className="admin-guard-actions">
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/login">Sign In</Link>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/">Back to Home</Link>
              </div>
            </div>
          )}

          {!loading && isAdmin && (
            <form className="tm-surface admin-section admin-form send-email-form" onSubmit={handleSubmit}>
              <div className="admin-section-header">
                <h3>Compose email</h3>
                <span className="send-email-recipient-count">
                  {recipientCount} recipient{recipientCount === 1 ? '' : 's'}
                </span>
              </div>

              <div className="form-row">
                <label htmlFor="send-email-audience">Recipients</label>
                <select
                  id="send-email-audience"
                  value={audience}
                  onChange={(event) => setAudience(event.target.value as Audience)}
                  disabled={sending}
                >
                  <option value="verified">
                    Verified users ({audienceCounts.verified_users})
                  </option>
                  <option value="all">All users ({audienceCounts.all_users})</option>
                  <option value="specific">A specific email address</option>
                </select>
                <span className="send-email-help">
                  Each recipient receives a separate email, so addresses are never shared.
                </span>
              </div>

              {audience === 'specific' && (
                <div className="form-row">
                  <label htmlFor="send-email-specific">Recipient email address</label>
                  <input
                    id="send-email-specific"
                    type="email"
                    value={specificEmail}
                    onChange={(event) => setSpecificEmail(event.target.value)}
                    placeholder="person@example.com"
                    required
                    disabled={sending}
                  />
                  <span className="send-email-help">
                    This can be any valid email address; it does not need to belong to a user.
                  </span>
                </div>
              )}

              <div className="form-row">
                <label htmlFor="send-email-from">From</label>
                <input
                  id="send-email-from"
                  type="email"
                  value={fromEmail}
                  onChange={(event) => setFromEmail(event.target.value)}
                  required
                  disabled={sending}
                />
              </div>

              <div className="form-row">
                <label htmlFor="send-email-subject">Subject</label>
                <input
                  id="send-email-subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  maxLength={200}
                  required
                  disabled={sending}
                />
              </div>

              <div className="form-row">
                <label htmlFor="send-email-text">Text body</label>
                <textarea
                  id="send-email-text"
                  value={textBody}
                  onChange={(event) => setTextBody(event.target.value)}
                  rows={10}
                  placeholder="Plain-text version of the email"
                  disabled={sending}
                />
              </div>

              <div className="form-row">
                <label htmlFor="send-email-html">HTML body</label>
                <textarea
                  id="send-email-html"
                  className="send-email-html-input"
                  value={htmlBody}
                  onChange={(event) => setHtmlBody(event.target.value)}
                  rows={14}
                  placeholder="<h1>Hello!</h1>"
                  disabled={sending}
                />
              </div>

              {error && <div className="send-email-message send-email-error">{error}</div>}
              {result && (
                <div
                  className={`send-email-message ${result.success ? 'send-email-success' : 'send-email-warning'}`}
                  role="status"
                >
                  Sent {result.sent_count} of {result.recipient_count} emails.
                  {result.failed_count > 0 && (
                    <div>
                      Failed: {result.failed_recipients.join(', ')}
                    </div>
                  )}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="tm-button tm-button--primary ml-replace-btn send-email-submit"
                  disabled={sending || recipientCount === 0}
                >
                  {sending ? 'Sending…' : `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default SendEmailPage;
